import React from 'react';
import { useNavigate, useSearchParams, matchPath, useLocation } from 'react-router-dom';
import {useNavigateBack, getLocationReturnPath } from '../../../hooks/useNavigateBack';
import useSWR from 'swr';
import { useTheme } from '../../../contexts/ThemeContext';
import { useNotification } from '../../../contexts/NotificationContext';
import { cn } from '../../../lib/utils';
import { fetcher } from '../../../services/fetcher';
import { createQuotationEvaluation, updateQuotationEvaluation } from '../../../services/mutations';
import {
  Button,
  Card,
  ChevronLeftIcon,
  DateInput,
  Label,
  Textarea,
  TextInput,
} from '../../../components/ui';
import { moduleConfig as caseModuleConfig } from '../supply-cases/config';
import { moduleConfig } from './quotationEvaluationsModuleConfig';
import { useQuotationEvaluation } from './useData';

type CaseViewQuotation = {
  id: number;
  ref?: string | null;
  quoDate?: string | null;
  enqId: number;
  supplier?: { name?: string | null; code?: string | null } | null;
  currency?: { code: string; symbol?: string | null } | null;
};

const Form: React.FC = () => {
  const { colors } = useTheme();
  const { showError, showSuccess } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const editMatch = matchPath(
    { path: '/supplies/quotation-evaluations/:id/edit', end: true },
    location.pathname,
  );
  const isEdit = editMatch != null;
  const editId = editMatch?.params.id != null && /^\d+$/.test(editMatch.params.id) ? Number(editMatch.params.id) : undefined;

  const caseIdParam = searchParams.get('caseId');
  const caseIdFromQuery =
    caseIdParam != null && /^\d+$/.test(caseIdParam) ? Number(caseIdParam) : undefined;

  const { item: existing, isLoading: loadingEval } = useQuotationEvaluation(isEdit ? editId : undefined);

  const [caseId, setCaseId] = React.useState<number | undefined>(caseIdFromQuery);
  const caseViewKey = caseId != null && caseId > 0 ? `/supply-cases/${caseId}/case-view` : null;
  const { data: caseView, isLoading: loadingCase } = useSWR(caseViewKey, fetcher);
  const quotations: CaseViewQuotation[] = React.useMemo(
    () => (caseView as { quotations?: CaseViewQuotation[] } | undefined)?.quotations ?? [],
    [caseView],
  );

  const [selectedIds, setSelectedIds] = React.useState<number[]>([]);
  const [form, setForm] = React.useState({
    evaluationDate: '',
    ref: '',
    notes: '',
  });
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (isEdit && existing?.caseId) {
      setCaseId(existing.caseId);
    } else if (!isEdit && caseIdFromQuery) {
      setCaseId(caseIdFromQuery);
    }
  }, [isEdit, existing?.caseId, caseIdFromQuery]);

  React.useEffect(() => {
    if (!isEdit || !existing) return;
    setForm({
      evaluationDate: existing.evaluationDate ? String(existing.evaluationDate).slice(0, 10) : '',
      ref: existing.ref || '',
      notes: existing.notes || '',
    });
    const qids = [
      existing.quotation1Id,
      existing.quotation2Id,
      existing.quotation3Id,
      existing.quotation4Id,
      existing.quotation5Id,
    ].filter((x): x is number => x != null);
    setSelectedIds(qids);
  }, [isEdit, existing]);

  const availableToAdd = React.useMemo(
    () => quotations.filter((q) => !selectedIds.includes(q.id) && selectedIds.length < 5),
    [quotations, selectedIds],
  );

  const addQuotation = (id: number) => {
    if (selectedIds.length >= 5 || selectedIds.includes(id)) return;
    setSelectedIds((s) => [...s, id]);
  };

  const removeAt = (idx: number) => {
    setSelectedIds((s) => s.filter((_, i) => i !== idx));
  };

  const move = (idx: number, dir: -1 | 1) => {
    setSelectedIds((s) => {
      const j = idx + dir;
      if (j < 0 || j >= s.length) return s;
      const next = [...s];
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  };

  const sc = (caseView as { supplyCase?: { ref?: string | null; id: number } } | undefined)?.supplyCase;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (caseId == null || caseId <= 0) {
      showError('Select a case (open this screen from a supply case, or add ?caseId=).');
      return;
    }
    if (selectedIds.length < 2) {
      showError('Pick at least two quotations to compare.');
      return;
    }
    setSaving(true);
    try {
      if (isEdit && editId != null) {
        await updateQuotationEvaluation(editId, {
          evaluationDate: form.evaluationDate || undefined,
          ref: form.ref || undefined,
          notes: form.notes || undefined,
          quotationIds: selectedIds,
        });
        showSuccess('Evaluation updated.');
        navigate(moduleConfig.module.route.detail(editId));
      } else {
        const res = await createQuotationEvaluation({
          caseId,
          quotationIds: selectedIds,
          evaluationDate: form.evaluationDate || undefined,
          ref: form.ref || undefined,
          notes: form.notes || undefined,
        });
        showSuccess('Evaluation created.');
        navigate(moduleConfig.module.route.detail(res.id));
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message;
      showError(
        Array.isArray(msg) ? msg.join(' ') : typeof msg === 'string' ? msg : 'Could not save evaluation.',
      );
    } finally {
      setSaving(false);
    }
  };

  const formBackFallback =
    caseId != null ? caseModuleConfig.module.route.caseView(caseId) : caseModuleConfig.module.route.list;
  const goBack = useNavigateBack(formBackFallback, getLocationReturnPath(location.state));

  if (isEdit && loadingEval) {
    return <p className={cn('text-sm', colors.textSecondary)}>Loading…</p>;
  }
  if (isEdit && !loadingEval && !existing) {
    return <p className={cn('text-sm', colors.textSecondary)}>Evaluation not found.</p>;
  }

  return (
    <div className="space-y-5">
      <Card
        variant="outlined"
        className={cn('flex flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-5', 'border shadow-sm')}
      >
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={cn('-ml-2 gap-1.5', colors.text)}
          onClick={goBack}
        >
          <ChevronLeftIcon className="h-4 w-4" />
          Back
        </Button>
        <h1 className={cn('text-lg font-semibold', colors.text)}>{isEdit ? 'Edit evaluation' : 'New quotation evaluation'}</h1>
      </Card>

      <form onSubmit={onSubmit} className="space-y-4">
        <Card variant="default" className="p-4 md:p-5">
          <h2 className={cn('mb-3 text-xs font-semibold uppercase tracking-wider', colors.textSecondary)}>Header</h2>
          {caseId != null && sc ? (
            <p className={cn('mb-3 text-sm', colors.textSecondary)}>
              Case:{' '}
              <span className={cn('font-medium', colors.text)}>
                {sc.ref || `Case #${sc.id}`}
              </span>
            </p>
          ) : !isEdit ? (
            <p className="mb-3 text-sm text-green-700 dark:text-green-300">
              Missing case. Use “New evaluation” from a supply case, or add <code className="text-xs">?caseId=</code> to
              the URL.
            </p>
          ) : null}

          <div className="grid gap-4 sm:grid-cols-2 sm:items-start">
            <div className="flex flex-col gap-1">
              <Label htmlFor="ev-date" className="leading-5">
                Evaluation date
              </Label>
              <DateInput
                id="ev-date"
                className="h-10 min-h-[2.5rem] w-full"
                value={form.evaluationDate}
                onChange={(e) => setForm((f) => ({ ...f, evaluationDate: e.target.value }))}
                showFormatHint={false}
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="ev-ref" className="leading-5">
                Reference
              </Label>
              <TextInput
                id="ev-ref"
                className="h-10 min-h-[2.5rem]"
                value={form.ref}
                onChange={(e) => setForm((f) => ({ ...f, ref: e.target.value }))}
                maxLength={30}
              />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="ev-notes">Notes</Label>
              <Textarea
                id="ev-notes"
                className="mt-1 min-h-[80px]"
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                maxLength={250}
              />
            </div>
          </div>
        </Card>

        <Card variant="default" className="p-4 md:p-5">
          <h2 className={cn('mb-2 text-xs font-semibold uppercase tracking-wider', colors.textSecondary)}>Quotations</h2>
          <p className={cn('mb-3 text-sm', colors.textSecondary)}>
            Add two to five quotations in column order (left to right in the comparison). Same supply case only.
          </p>
          {caseId == null || loadingCase ? (
            <p className={cn('text-sm', colors.textSecondary)}>Loading case quotations…</p>
          ) : (
            <div className="grid gap-4 lg:grid-cols-2">
              <div className={cn('rounded-lg border p-3', colors.border, colors.surfaceHover)}>
                <p className={cn('mb-2 text-xs font-semibold uppercase tracking-wider', colors.textSecondary)}>
                  Available quotations
                </p>
                {availableToAdd.length === 0 ? (
                  <p className={cn('text-sm', colors.textSecondary)}>
                    {selectedIds.length >= 5 ? 'Maximum 5 quotations selected.' : 'No more quotations to add.'}
                  </p>
                ) : (
                  <div className="space-y-2">
                    {availableToAdd.map((q) => {
                      const s = q.supplier;
                      const su = s ? (s.name) : '';
                      return (
                        <button
                          key={q.id}
                          type="button"
                          onClick={() => addQuotation(q.id)}
                          className={cn(
                            'w-full rounded-lg border px-3 py-2 text-left transition',
                            'hover:bg-black/[0.03] dark:hover:bg-white/[0.04]',
                            colors.border,
                          )}
                        >
                          <div className={cn('text-sm font-medium', colors.text)}>{q.ref || `Q-${q.id}`}</div>
                          <div className={cn('text-xs', colors.textSecondary)}>
                            {su} · {q.currency?.code ?? ''}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className={cn('rounded-lg border p-3', colors.border)}>
                <p className={cn('mb-2 text-xs font-semibold uppercase tracking-wider', colors.textSecondary)}>
                  Selected columns (left to right)
                </p>
                {selectedIds.length === 0 ? (
                  <p className={cn('text-sm', colors.textSecondary)}>No quotations selected yet.</p>
                ) : (
                  <div className="space-y-2">
                    {selectedIds.map((qid, idx) => {
                      const q = quotations.find((x) => x.id === qid);
                      const s = q?.supplier;
                      const su = s ? (s.name) : '';
                      return (
                        <div
                          key={qid}
                          className={cn('flex flex-wrap items-center justify-between gap-2 rounded-lg border px-3 py-2', colors.border)}
                        >
                          <div className="min-w-0">
                            <div className={cn('text-xs font-semibold uppercase tracking-wider', colors.textSecondary)}>
                              Column {idx + 1}
                            </div>
                            <div className={cn('truncate text-sm font-medium', colors.text)}>{q?.ref || `Q-${qid}`}</div>
                            <div className={cn('truncate text-xs', colors.textSecondary)}>
                              {su} · {q?.currency?.code ?? ''}
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-1">
                            <Button
                              type="button"
                              size="icon"
                              variant="outline"
                              onClick={() => move(idx, -1)}
                              disabled={idx === 0}
                              aria-label="Move up"
                              title="Move up"
                            >
                              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor">
                                <path d="M10 4l5 6H5l5-6z" fill="currentColor" />
                              </svg>
                            </Button>
                            <Button
                              type="button"
                              size="icon"
                              variant="outline"
                              onClick={() => move(idx, 1)}
                              disabled={idx === selectedIds.length - 1}
                              aria-label="Move down"
                              title="Move down"
                            >
                              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor">
                                <path d="M10 16l-5-6h10l-5 6z" fill="currentColor" />
                              </svg>
                            </Button>
                            <Button
                              type="button"
                              size="icon"
                              variant="ghost"
                              onClick={() => removeAt(idx)}
                              aria-label="Remove"
                              title="Remove"
                            >
                              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor">
                                <path
                                  d="M6 6l8 8M14 6l-8 8"
                                  strokeWidth="1.8"
                                  strokeLinecap="round"
                                />
                              </svg>
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </Card>

        <div className="flex flex-wrap gap-2">
          <Button type="submit" disabled={saving} variant="default">
            {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create evaluation'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default Form;
